import { isAppVersionGreaterThanOrEqual, kAndroidSafeAreaVersion } from './versionUtils';

/**
 * 平台类型常量
 */
export enum MapBoxPlatform {
  /** Web 平台 */
  WEB = 'web',
  /** iOS 平台 */
  IOS = 'ios',
  /** Android 平台 */
  ANDROID = 'android',
  /** 未知平台 */
  UNKNOWN = 'unknown'
}

/**
 * 设备ID生成和获取工具
 */
export const DEVICE_ID_KEY = 'device_id';

/**
 * 生成设备ID
 * 基于浏览器指纹和随机数生成唯一设备标识
 * 包含完整的异常处理，确保在各种环境下都能正常工作
 */
const generateDeviceId = (): string => {
    try {
        // 安全获取Canvas指纹
        let canvasFingerprint = 'canvas_unavailable';
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.textBaseline = 'top';
                ctx.font = '14px Arial';
                ctx.fillText('Device fingerprint', 2, 2);
                canvasFingerprint = canvas.toDataURL();
            }
        } catch (canvasError) {
            console.warn('Canvas fingerprint generation failed:', canvasError);
        }
        
        // 安全获取浏览器信息
        const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown_ua';
        const language = (typeof navigator !== 'undefined' && navigator.language) || 'unknown_lang';
        
        // 安全获取屏幕信息
        let screenInfo = 'unknown_screen';
        try {
            // 使用window.screen避免ESLint的no-restricted-globals警告
            if (typeof window !== 'undefined' && window.screen && window.screen.width && window.screen.height) {
                screenInfo = window.screen.width + 'x' + window.screen.height;
            }
        } catch (screenError) {
            console.warn('Screen info access failed:', screenError);
        }
        
        // 安全获取时区信息
        let timezoneOffset = 0;
        try {
            timezoneOffset = new Date().getTimezoneOffset();
        } catch (dateError) {
            console.warn('Timezone offset access failed:', dateError);
        }
        
        // 构建指纹字符串
        const fingerprint = [
            userAgent,
            language,
            screenInfo,
            timezoneOffset.toString(),
            canvasFingerprint.slice(-50), // 取canvas指纹的后50个字符
            Math.random().toString(36).substring(2, 15) // 添加随机数确保唯一性
        ].join('|');
        
        // 生成简单的哈希
        let hash = 0;
        for (let i = 0; i < fingerprint.length; i++) {
            const char = fingerprint.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // 转换为32位整数
        }
        
        // 转换为正数并添加时间戳确保唯一性
        const timestamp = Date.now().toString(36);
        const deviceId = Math.abs(hash).toString(36) + timestamp;
        return deviceId;
        
    } catch (error) {
        // 如果所有方法都失败，使用纯随机方式生成
        console.error('Device ID generation failed, using fallback method:', error);
        return 'fallback_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    }
};

/**
 * 获取设备ID
 * 如果本地存储中没有设备ID，则生成一个新的
 */
export const getDeviceId = (): string => {
    try {
        let deviceId = localStorage.getItem(DEVICE_ID_KEY);
        
        if (!deviceId) {
            deviceId = generateDeviceId();
            localStorage.setItem(DEVICE_ID_KEY, deviceId);
        }
        
        return deviceId;
    } catch (error) {
        console.error('Failed to get device ID:', error);
        // 如果localStorage不可用，返回基于当前会话的临时ID
        return 'temp_' + generateDeviceId();
    }
};

/**
 * 重置设备ID（用于测试或特殊情况）
 */
export const resetDeviceId = (): string => {
    try {
        localStorage.removeItem(DEVICE_ID_KEY);
        return getDeviceId();
    } catch (error) {
        console.error('Failed to reset device ID:', error);
        return 'temp_' + generateDeviceId();
    }
};

// 检测是否为Android设备
export const isAndroid = (): boolean => {
    return typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);
};

// 检测是否为iOS设备（含 iPadOS 13+ 在 Safari 中声明为 Mac 的触控机型）
export const isIOS = (): boolean => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream) {
        return true;
    }
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

// Android状态栏相关配置
const ANDROID_STATUS_BAR_HEIGHT = 24; // 默认状态栏高度为24px
const ANDROID_BOTTOM_SAFE_AREA = 8; // Android底部安全区域高度

const CACHE_KEY = 'android_status_bar_height';

// 获取状态栏高度（同步）
const getStatusBarHeight = (): number => {
    // 先从localStorage读取缓存
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached !== null) {
        return parseInt(cached, 10);
    }
    
    // 如果没有缓存，返回默认值0（iOS或未初始化的Android）
    return 0;
};

// 同步获取导航栏容器样式
export const getNavBarContainerStyle = (className: string = '') => {
    const statusBarHeight = getStatusBarHeight();
    return {
        className: className.trim(),
        style: {
            paddingTop: `${statusBarHeight}px`
        }
    };
};

// 初始化函数：在应用启动时调用，计算并缓存状态栏高度
export const initializeNavBarStyle = async () => {
    try {
        let statusBarHeight = 0;
        if (isAndroid()) {
            const isVersion31OrHigher = await isAppVersionGreaterThanOrEqual(kAndroidSafeAreaVersion);
            statusBarHeight = isVersion31OrHigher ? 0 : ANDROID_STATUS_BAR_HEIGHT;
        }
        localStorage.setItem(CACHE_KEY, statusBarHeight.toString());
    } catch (error) {
        console.warn('Failed to initialize nav bar style:', error);
        localStorage.setItem(CACHE_KEY, '0');
    }
};


// 获取底部导航栏容器样式（包含className和style）
export const getBottomNavBarContainerStyle = (className: string = '') => {
    return {
        className: className.trim(),
        style: {
            paddingBottom: isAndroid() ? `${ANDROID_BOTTOM_SAFE_AREA}px` : 'env(safe-area-inset-bottom)' // 适配底部安全区域
        }
    };
};

/**
 * 检测当前运行平台类型
 * @returns 平台类型枚举值
 */
export const detectPlatform = (): MapBoxPlatform => {
    try {
        // 检查是否在 React Native 环境中（移动端）
        if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
            // 在 React Native 中，可以通过 Platform.OS 判断
            // 但这里是 web 项目，暂时返回 web
            return MapBoxPlatform.WEB;
        }

        // Web 环境下的检测
        if (typeof navigator !== 'undefined' && navigator.userAgent) {
            // 使用现有的 isAndroid 函数
            if (isAndroid()) {
                return MapBoxPlatform.ANDROID;
            }

            // 使用现有的 isIOS 函数
            if (isIOS()) {
                return MapBoxPlatform.IOS;
            }
        }

        // 默认返回 web
        return MapBoxPlatform.WEB;
    } catch (error) {
        // 如果检测过程中出现异常，返回 unknown
        console.warn('Failed to detect platform:', error);
        return MapBoxPlatform.UNKNOWN;
    }
};
